//dependencies
import { findLastIndex } from "lodash";
import React, { ForwardedRef, ReactNode, useEffect, useState } from "react";

import { InputWrapper, TitleLabel } from "@performyard/py-components";

//components
import TagItem from "./TagItem";

//utils
import isArray from "../../../utils/IsArray";
import fixedForwardRef from "../../../utils/fixedForwardRef";

// css
import css from "./TagList.module.scss";

export type TagListOption<T> = {
	value: T;
	label: ReactNode | string;
	//if ariaLabel is not provided, it will default to the label
	ariaLabel?: string;
	disabled?: boolean;
};

type TagListProps<T, M extends boolean> = {
	/**
	 * List of options to be displayed in the tag list
	 */
	options?: TagListOption<T>[];
	/**
	 * The value of the tag list
	 */
	value: M extends true ? T[] : T | null;
	/**
	 * Function to be called when the value of the tag list changes
	 */
	onChange: M extends true ? (values: T[]) => void : (value: T | null) => void;
	/**
	 * If true, multiple tags can be selected
	 */
	multiple?: M;
	/**
	 * If true, the tag list is disabled
	 */
	disabled?: boolean;
	/**
	 * Label for the tag list
	 */
	label?: string;
	/**
	 * Error message to be displayed
	 */
	errorMessage?: string;
	/**
	 * Id of the tag list
	 */
	inputId?: string;
	/**
	 * If true, the error message will be animated
	 */
	animatedError?: boolean;
	/**
	 * If true, the active tag will be animated
	 */
	animatedActive?: boolean;
	/**
	 * Image of the title
	 */
	titleImage?: string;
	/**
	 * href of the title
	 */
	titleHref?: string;
	titleTooltip?: string;
};
/**
 * @deprecated Use TagSelectMultiple or TagSelectSingle instead
 */
const TagList = <T, multiple extends boolean>(
	{
		options,
		value,
		onChange,
		multiple,
		disabled = false,
		label,
		errorMessage,
		inputId = "tag-list",
		animatedActive,
		titleImage,
		titleHref,
		titleTooltip,
	}: TagListProps<T, multiple>,
	ref: ForwardedRef<HTMLUListElement>
) => {
	// is list group focused
	const [isFocused, setIsFocused] = useState(false);
	const [focusedItemIndex, setFocusedItemIndex] = useState<null | number>(null);

	const handleOnChange = (optionValue: T) => {
		if (disabled) {
			return;
		}
		if (multiple) {
			const existingValues = value as T[];
			const newValue = existingValues.includes(optionValue)
				? existingValues.filter((v) => v !== optionValue)
				: [...existingValues, optionValue];
			onChange(newValue as T[] & T);
		} else {
			const currentValue = value as T | null;
			onChange((currentValue === optionValue ? null : optionValue) as (T | null) & T[]);
		}
	};

	const handleBulkSelection = (optionValues: T[]) => {
		if (disabled) {
			return;
		}
		if (multiple) {
			onChange(optionValues as T[] & T);
		}
	};

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (
				e.key !== "Tab" &&
				isFocused &&
				!disabled &&
				isArray(options) &&
				options?.some((option) => !option.disabled)
			) {
				e.preventDefault();
				const firstIndex = options?.findIndex((option) => !option.disabled);
				// get last index of options that is not disabled
				const lastIndex = findLastIndex(options, (option) => !option.disabled);
				if (e.key === "ArrowDown" || e.key === "ArrowRight") {
					// get next non disabled index, if there are no more non disabled options, go to the firstIndex
					let nextIndex: number | null = null;
					if (focusedItemIndex === null) {
						nextIndex = firstIndex;
					} else {
						const remainingOptions = options!.slice(focusedItemIndex + 1, options.length);
						nextIndex = remainingOptions.findIndex((option) => !option.disabled);
						if (nextIndex === -1) {
							nextIndex = firstIndex;
						} else {
							nextIndex += focusedItemIndex + 1;
						}
					}
					setFocusedItemIndex(nextIndex);
				} else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
					// get previous non disabled index, if there are no more non disabled options, go to the lastIndex
					let nextIndex: number | null = null;
					if (focusedItemIndex === null) {
						nextIndex = lastIndex;
					} else {
						const remainingOptions = options!.slice(0, focusedItemIndex);
						nextIndex = findLastIndex(remainingOptions, (option) => !option.disabled);
						if (nextIndex === -1) {
							nextIndex = lastIndex;
						}
					}

					setFocusedItemIndex(nextIndex);
				} else if (e.key === "Enter") {
					if (
						focusedItemIndex !== null &&
						options![focusedItemIndex] &&
						!options![focusedItemIndex].disabled
					) {
						handleOnChange(options![focusedItemIndex].value);
					}
				} else if (multiple && e.key === "Escape") {
					// bulk deselect all items when focused and escape is pressed
					handleBulkSelection([]);
				} else if (multiple && (e.key === "a" || e.key === "A") && (e.ctrlKey || e.metaKey)) {
					// bulk select all items when focused and ctrl + a is pressed
					handleBulkSelection(
						options ? options?.filter((option) => !option.disabled)?.map((option) => option.value) : []
					);
				}
			}
		};

		const handleClick = () => {
			setFocusedItemIndex(null);
		};

		document.addEventListener("keydown", handleKeyDown);
		document.addEventListener("click", handleClick);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.removeEventListener("click", handleClick);
		};
	}, [isFocused, focusedItemIndex, isFocused, JSON.stringify(value)]);

	return (
		<InputWrapper
			label={
				label && (titleHref || titleImage) ? (
					<TitleLabel
						label={label}
						titleHref={titleHref}
						titleTooltip={titleTooltip}
						titleImage={titleImage}
					/>
				) : (
					label
				)
			}
			error={errorMessage}
			id={inputId}
		>
			<ul
				ref={ref}
				className={css.tagList}
				id={inputId}
				data-testid={inputId}
				tabIndex={0}
				aria-live="polite"
				data-selection-mode={multiple ? "multiple" : "single"}
				role="grid"
				aria-disabled={disabled}
				// onFocus set to true
				onFocusCapture={() => {
					setIsFocused(disabled ? false : true);
					setFocusedItemIndex(focusedItemIndex);
				}}
				// on unfocus set to false
				onBlurCapture={() => {
					setIsFocused(false);
				}}
			>
				{options?.map((option, index) => {
					return (
						<li key={`${index}-${JSON.stringify(option.value)}`}>
							<TagItem
								value={option.value as T}
								onClick={handleOnChange}
								selected={
									value && multiple ? (value as T[])?.includes(option.value) : value === option.value
								}
								ariaLabel={option?.ariaLabel || option.label?.toString()}
								disabled={disabled || option?.disabled}
								focused={index === focusedItemIndex}
								animatedActive={animatedActive}
							>
								{option.label}
							</TagItem>
						</li>
					);
				})}
			</ul>
		</InputWrapper>
	);
};

export default fixedForwardRef(TagList);
